import { Component, DestroyRef, computed, effect, inject, Input, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenav } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@features/auth/services/auth.service';
import { Fleet } from '@features/company/views/fleets/models/fleet-row.model';
import { CompanyService } from '@features/company/services/company.service';
import { FleetService } from '@features/company/views/fleets/services/fleets.service';
import {
  buildRouterFleetQueryParams,
  fleetSelectionsEqual,
  normalizeSelectedFleetIds,
  readFleetSelectionFromRoute,
} from '@core/utils/fleet-filter-params.util';
import { SelectMaterialComponent } from '@shared/components/selects/select-material/select-material';
import { map, of } from 'rxjs';
import { MenuInfoUserComponent } from './menu-info-user/menu-info-user.component';

@Component({
  selector: 'app-menu-navbar',
  imports: [
    MatIconModule,
    MatButtonModule,
    MatToolbarModule,
    MatSlideToggleModule,
    MatTooltipModule,
    SelectMaterialComponent,
    MenuInfoUserComponent,
  ],
  templateUrl: './menu-navbar.html',
  styleUrl: './menu-navbar.scss',
})
export class MenuNavbarComponent {
  @Input() menuLateral!: MatSidenav;

  fleetService = inject(FleetService);
  private readonly companyService = inject(CompanyService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  _router = inject(Router);
  _authService = inject(AuthService);

  darkMode = signal(false);
  /** Bloquea la re-aplicación de `fleetId` desde la URL mientras el usuario edita el select. */
  private readonly syncingFleetUrl = signal(false);
  /** Valor local del select; evita que el padre restaure la flota al quitar un chip. */
  private readonly fleetSelectModel = signal<string[] | null>(null);

  applyDarkMode = effect(() => {
    const darkMode = this.darkMode();
    document.body.classList.toggle('dark-theme', darkMode);
  });

  private readonly syncFleetSelectFromService = effect(() => {
    if (this.syncingFleetUrl()) {
      return;
    }

    const ids = normalizeSelectedFleetIds(this.fleetService.selectedFleetIds());
    this.fleetSelectModel.set(ids.length > 0 ? ids : null);
  });

  private readonly fleetsResource = rxResource({
    params: () => ({ businessId: this.companyService.businessId() }),
    stream: ({ params }) => {
      if (!params.businessId) {
        return of([] as Fleet[]);
      }

      return this.fleetService.getAll({
        businessId: params.businessId,
        orderBy: ['name,0'],
      });
    },
    defaultValue: [] as Fleet[],
  });

  fleetOptions(): Fleet[] {
    return this.fleetsResource.value() ?? [];
  }

  fleetOptionsLoading(): boolean {
    return this.fleetsResource.isLoading();
  }

  /** Valor del multiselect de flota para el template. */
  fleetSelectValue(): string[] | null {
    return this.fleetSelectModel();
  }

  /** Una sola flota: el select se compacta; con 0 o 2+ se mantiene el ancho actual. */
  readonly isSingleFleetSelected = computed(() => {
    const value = this.fleetSelectModel();
    return Array.isArray(value) && value.length === 1;
  });

  private readonly syncFleetSelectionWhenOptionsReady = effect(() => {
    if (this.syncingFleetUrl()) {
      return;
    }

    const fleetList = this.fleetOptions();
    const businessId = this.companyService.businessId();
    if (!businessId || fleetList.length === 0) {
      return;
    }

    const selectionFromUrl = readFleetSelectionFromRoute(this.route.snapshot.queryParamMap);
    if (selectionFromUrl === null) {
      return;
    }

    const urlIds = normalizeSelectedFleetIds(selectionFromUrl);
    const validIds = urlIds.filter((id) => fleetList.some((fleet) => fleet.id === id));
    if (validIds.length === 0) {
      return;
    }

    const normalizedSelection = validIds.length === 1 ? validIds[0] : validIds;
    if (fleetSelectionsEqual(normalizedSelection, this.fleetService.selectedFleetIds())) {
      return;
    }

    this.syncingFleetUrl.set(true);
    this.fleetService.selectedFleetIds.set(normalizedSelection);
    this.fleetSelectModel.set(validIds);
    this.syncingFleetUrl.set(false);
  });

  constructor() {
    const initialSelection = readFleetSelectionFromRoute(this.route.snapshot.queryParamMap);
    if (initialSelection !== null) {
      const initialIds = normalizeSelectedFleetIds(initialSelection);
      this.fleetService.selectedFleetIds.set(initialSelection);
      this.fleetSelectModel.set(initialIds.length > 0 ? initialIds : null);
    }

    this.route.queryParamMap
      .pipe(
        map((params) => readFleetSelectionFromRoute(params)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((selectionFromUrl) => {
        if (this.syncingFleetUrl()) {
          return;
        }

        const currentSelection = this.fleetService.selectedFleetIds();

        if (fleetSelectionsEqual(selectionFromUrl, currentSelection)) {
          return;
        }

        if (selectionFromUrl !== null) {
          this.syncingFleetUrl.set(true);
          this.fleetService.selectedFleetIds.set(selectionFromUrl);
          this.fleetSelectModel.set(normalizeSelectedFleetIds(selectionFromUrl));
          this.syncingFleetUrl.set(false);
          return;
        }

        const currentIds = normalizeSelectedFleetIds(currentSelection);
        if (currentIds.length > 0) {
          void this.syncFleetUrl(currentSelection);
        }
      });
  }

  private syncFleetUrl(selection: string | string[] | null): void {
    this.syncingFleetUrl.set(true);
    void this.router
      .navigate([], {
        queryParams: buildRouterFleetQueryParams(selection),
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
      .finally(() => this.syncingFleetUrl.set(false));
  }

  onFleetChange(event: string | string[] | null) {
    const ids = normalizeSelectedFleetIds(event);
    const normalizedSelection =
      ids.length === 0 ? null : ids.length === 1 ? ids[0] : ids;

    this.syncingFleetUrl.set(true);
    this.fleetSelectModel.set(ids.length > 0 ? ids : null);
    this.fleetService.selectedFleetIds.set(normalizedSelection);
    this.syncFleetUrl(normalizedSelection);
  }

  logout() {
    this._authService.signout();
  }
}
